// Port of SuiteModel/Fields.swift's rules (design-docs/Plot grid — design.md): the ids every device derives for a field
// or option made under a name, which of the app's own fields the writer may change, and how a value reads. The edits
// themselves are `StoreEditor`'s (addField, renameOption, …), which change a field's options list element by element so
// two devices' edits merge.
import type { FieldType, InkColor, MetadataValue } from '../contract.ts';
import { stableUUID } from '../hashing.ts';
import type { MetadataField } from '../model/documents.ts';
import { displayString } from './savedSearch.ts';

type Option = MetadataField['options'][number];

export const FIELD_NAME_LIMIT = 60;

/** `MetadataField.normalized`: trimmed, inner runs of whitespace made one space. */
export const normalizedFieldName = (name: string) => name.split(/\p{White_Space}+/u).filter((w) => w !== '').join(' ');

/** `MetadataField.id(for:)`: a field made under a name has the same id on every device. */
export const fieldID = (name: string) => stableUUID('field:' + normalizedFieldName(name).toLowerCase());

/** `MetadataOption.id(for:in:)`. */
export const optionID = (name: string, field: string) => stableUUID('option:' + field + ':' + normalizedFieldName(name).toLowerCase());

/** `MetadataField.key`: what two spellings of one name share ("Polarity", "polarity "). */
export const fieldKey = (name: string) => normalizedFieldName(name).normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

/** `MetadataField.standardIDs`: the app's own fields. */
export const STANDARD_FIELD_IDS: ReadonlySet<string> = new Set([
  'status', 'label', 'pov', 'word-target', 'section-type', 'beat', 'post-number', 'publish-date', 'tags', 'story-time',
  'story-duration', 'setting',
]);

export const isStandardField = (f: Pick<MetadataField, 'id'>) => STANDARD_FIELD_IDS.has(f.id);
export const canRenameField = (f: Pick<MetadataField, 'id'>) => !isStandardField(f) || f.id === 'status' || f.id === 'label';
export const canRemoveField = (f: Pick<MetadataField, 'id'>) => !isStandardField(f);
/** A list whose options the writer edits in the Fields sheet. The beat sheet owns the Beat field's. */
export const canEditOptions = (f: Pick<MetadataField, 'id' | 'type'>) => f.type === 'enum' && f.id !== 'beat';

/** `JournalPosts.statusOptions`' ids. */
const JOURNAL_STATUSES = new Set(['status-idea', 'status-draft', 'status-scheduled', 'status-published']);

/** `renameBlocked`: compile maps Part, Chapter and Scene by name. */
export function renameBlocked(field: Pick<MetadataField, 'id'>, option: Option): string | null {
  if (field.id !== 'section-type' || !['part', 'chapter', 'scene'].includes(option.name.toLowerCase())) return null;
  return `Compile sets parts, chapters and scenes by these names, so “${option.name}” stays.`;
}

/** `removalBlocked`: a journal's statuses number and date its posts. */
export function removalBlocked(field: Pick<MetadataField, 'id'>, option: Option): string | null {
  if (field.id === 'status' && JOURNAL_STATUSES.has(option.id)) return `“${option.name}” numbers and dates a journal's posts, so it stays.`;
  return renameBlocked(field, option);
}

/** `[MetadataField].visible`: all but the fields the writer removed. */
export const visibleFields = <F extends Pick<MetadataField, 'hidden'>>(fields: readonly F[]): F[] => fields.filter((f) => !f.hidden);

/** `named`: the field called `name`, however it's capitalised or spaced. */
export function fieldNamed<F extends Pick<MetadataField, 'name'>>(fields: readonly F[], name: string): F | undefined {
  const key = fieldKey(name);
  return fields.find((f) => fieldKey(f.name) === key);
}

/** `MetadataField.display`: a list field's option by name, empty for an option that's gone (never its id). */
export function fieldDisplay(field: Pick<MetadataField, 'type' | 'options'> | undefined, value: MetadataValue | undefined): string {
  if (!value) return '';
  if (field?.type === 'enum' && value.type === 'text') return field.options.find((o) => o.id === value.value)?.name ?? '';
  return displayString(value);
}

// MARK: Option colours (InkPalette.optionColor)

/** `InkPalette.text(_, dark: false)`: the writer's inks on Paper (app.css `--ink-*`). */
const PAPER_INKS: Record<InkColor, number> = {
  red: 0xb23a2e, orange: 0xb85e17, yellow: 0x8f7209, green: 0x3e7d3a, blue: 0x2f5e9e, pink: 0xae4577, purple: 0x6e4a9e, grey: 0x7a736b,
};

/** `%.4g`: four significant digits, trailing zeros dropped (the values are 0…1, so never an exponent). */
const g4 = (x: number) => (x === 0 ? '0' : x.toPrecision(4).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, ''));

/** `InkPalette.optionColor`: a list option's stored colour for an ink, "r g b" (0…1) as Scrivener's labels. */
export function optionColor(ink: InkColor): string {
  const rgb = PAPER_INKS[ink];
  return [rgb >> 16, rgb >> 8, rgb].map((c) => g4((c & 0xff) / 255)).join(' ');
}

/** `InkPalette.ink(ofOptionColor:)`: the ink a stored colour was made from, if it was (an imported label's is its own). */
export const inkOfOptionColor = (color: string | undefined): InkColor | null =>
  color === undefined ? null : (Object.keys(PAPER_INKS) as InkColor[]).find((ink) => optionColor(ink) === color) ?? null;

// MARK: Starter sets (StarterFields.swift)

export interface StarterFieldSet {
  id: string;
  name: string;
  summary: string;
  fields: { name: string; type: FieldType; options?: { name: string; ink?: InkColor }[] }[];
}

/** `StarterFieldSet.all`: ids come from the English names, so a set added on two devices is one set of fields. */
export const STARTER_FIELD_SETS: readonly StarterFieldSet[] = [
  { id: 'scene-questions', name: 'Scene questions', summary: 'Polarity, what changes and the turning point.', fields: [
    { name: 'Polarity', type: 'enum', options: [
      { name: '+', ink: 'green' }, { name: '−', ink: 'red' }, { name: '− → +', ink: 'green' }, { name: '+ → −', ink: 'red' }, { name: '±', ink: 'grey' },
    ] },
    { name: 'What changes', type: 'text' },
    { name: 'Turning point', type: 'text' },
  ] },
  { id: 'scene-shape', name: 'Scene shape', summary: 'Hook, build and payoff.', fields: [
    { name: 'Hook', type: 'text' }, { name: 'Build', type: 'text' }, { name: 'Payoff', type: 'text' },
  ] },
  { id: 'scene-turns', name: 'Scene turns', summary: 'Progressive complication, crisis, climax and resolution.', fields: [
    { name: 'Progressive complication', type: 'text' }, { name: 'Crisis', type: 'text' }, { name: 'Climax', type: 'text' }, { name: 'Resolution', type: 'text' },
  ] },
  { id: 'tracking', name: 'Tracking', summary: 'A Done box and a deadline.', fields: [
    { name: 'Done', type: 'boolean' }, { name: 'Deadline', type: 'date' },
  ] },
];

/** `StarterFieldSet.named`. */
export const starterSetNamed = (name: string): StarterFieldSet | undefined =>
  STARTER_FIELD_SETS.find((s) => s.id === name.toLowerCase() || fieldKey(s.name) === fieldKey(name));
